import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { api, getToken, setToken } from '@/lib/api'
import type { Profile, RolNivel } from '@/types/database'

interface AuthContextValue {
  session: { access_token: string } | null
  user: { id: string; email: string } | null
  profile: Profile | null
  rolNivel: RolNivel | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  registerPublic: (email: string, password: string, nombre: string, codigoAcceso: string) => Promise<{ error: string | null }>
  forgotPassword: (email: string, newPassword: string) => Promise<{ error: string | null }>
  isAdmin: boolean
  isCajero: boolean
  isDeposito: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

type MeResponse = {
  id: string; nombre: string; email: string; activo: boolean
  role_id: string; role_nombre: string; nivel: RolNivel
  deposito_id: string | null
}

function shapeProfile(raw: MeResponse): Profile {
  return {
    id:          raw.id,
    nombre:      raw.nombre,
    email:       raw.email,
    role_id:     raw.role_id,
    deposito_id: raw.deposito_id ?? null,
    activo:      raw.activo,
    created_at:  '',
    updated_at:  '',
    roles: {
      id:         raw.role_id,
      nombre:     raw.role_nombre as Profile['roles'] extends { nombre: infer N } ? N : never,
      nivel:      raw.nivel,
      created_at: '',
    },
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile]   = useState<Profile | null>(null)
  const [loading, setLoading]   = useState(true)

  async function loadProfile() {
    try {
      const raw = await api.get<MeResponse>('/api/auth/me')
      setProfile(shapeProfile(raw))
    } catch {
      // 401 ya es manejado por api.ts (borra token + redirige a /login)
      // Para errores de red o 502 (backend despertando): no borrar token
    }
  }

  useEffect(() => {
    if (getToken()) {
      loadProfile().finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function signIn(email: string, password: string): Promise<{ error: string | null }> {
    try {
      const data = await api.post<{ token: string; user: { userId: string; email: string } }>(
        '/api/auth/login',
        { email, password }
      )
      setToken(data.token)
      await loadProfile()
      return { error: null }
    } catch (e) {
      return { error: (e as Error).message ?? 'Error al iniciar sesión' }
    }
  }

  async function signOut() {
    setToken(null)
    setProfile(null)
    window.location.href = '/login'
  }

  async function registerPublic(email: string, password: string, nombre: string, codigoAcceso: string): Promise<{ error: string | null }> {
    try {
      await api.post('/api/auth/register-public', { email, password, nombre, codigoAcceso })
      return { error: null }
    } catch (e) {
      return { error: (e as Error).message ?? 'Error al registrar usuario' }
    }
  }

  async function forgotPassword(email: string, newPassword: string): Promise<{ error: string | null }> {
    try {
      await api.post('/api/auth/forgot-password', { email, newPassword })
      return { error: null }
    } catch (e) {
      return { error: (e as Error).message ?? 'Error al cambiar contraseña' }
    }
  }

  const rolNivel = (profile?.roles?.nivel ?? null) as RolNivel | null
  const currentToken = getToken()

  return (
    <AuthContext.Provider value={{
      session: currentToken ? { access_token: currentToken } : null,
      user: profile ? { id: profile.id, email: profile.email ?? '' } : null,
      profile,
      rolNivel,
      loading,
      signIn,
      signOut,
      registerPublic,
      forgotPassword,
      isAdmin:    rolNivel === 1,
      isCajero:   rolNivel !== null && rolNivel <= 2,
      isDeposito: rolNivel !== null && rolNivel <= 3,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return ctx
}
